import { jsPDF } from "jspdf";
import { CrackAnalysis, Severity, Finding, InspectionMode, TelemetryPoint, GEA_DEFECT_REGISTRY } from "../types";
import { calculateAssetGeometry, detectMissionStart } from "./telemetryService";

// ── GEA BRAND PALETTE ─────────────────────────────────────────────────────────
const GEA_BLUE: [number, number, number] = [0, 0, 169];    // #0000a9
const GEA_NAVY: [number, number, number] = [0, 0, 100];    // deep navy accent
const GEA_SLATE: [number, number, number] = [30, 41, 59];   // slate-800
const GEA_LIGHT: [number, number, number] = [241, 245, 249]; // slate-100
const GEA_MID: [number, number, number] = [148, 163, 184]; // slate-400
const GEA_DARK_TXT: [number, number, number] = [15, 23, 42];   // slate-900
const WHITE: [number, number, number] = [255, 255, 255];

const getDisplayTitle = (type: string) => {
  const match = GEA_DEFECT_REGISTRY.find(d => d.type === type || d.title === type);
  return match ? match.title : type;
};

const applyQuaternion = (v: { x: number, y: number, z: number }, q: { x: number, y: number, z: number, w: number }) => {
  const ix = (q.w * v.x) + (q.y * v.z) - (q.z * v.y);
  const iy = (q.w * v.y) + (q.z * v.x) - (q.x * v.z);
  const iz = (q.w * v.z) + (q.y * v.y) - (q.y * v.x);
  const iw = (-q.x * v.x) - (q.y * v.y) - (q.z * v.z);
  return {
    x: (ix * q.w) + (iw * -q.x) + (iy * -q.z) - (iz * -q.y),
    y: (iy * q.w) + (iw * -q.y) + (iz * -q.x) - (ix * -q.z),
    z: (iz * q.w) + (iw * -q.z) + (ix * -q.y) - (iy * -q.x)
  };
};

const hexToRgb = (hex: string): [number, number, number] => {
  let r = 0, g = 0, b = 0;
  // Handle 3-digit hex
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  // Handle 6-digit hex
  else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }
  return [r, g, b];
};

const getCategoryColor = (type: string): [number, number, number] => {
  const match = GEA_DEFECT_REGISTRY.find(d => d.type === type || d.title === type);
  if (match && match.hex) {
    return hexToRgb(match.hex);
  }
  return GEA_BLUE; // Fallback to GEA Blue
};

const formatMMSS = (s: number) => {
  const totalSecs = Math.floor(isNaN(s) ? 0 : s);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const shrinkImage = (base64: string, maxWidth: number = 800): Promise<string> => {
  return new Promise((resolve) => {
    if (!base64 || !base64.startsWith('data:image')) return resolve(base64);
    const img = new Image();
    img.onload = () => {
      if (img.width <= maxWidth) return resolve(base64);
      const canvas = document.createElement('canvas');
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(base64);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = () => resolve(base64);
    img.src = base64;
  });
};

const calculatePreciseCoverage = (points: TelemetryPoint[], contextPoints?: TelemetryPoint[], useExplicitPoints: boolean = false) => {
  if (!points || points.length < 2) return { coverageStr: "N/A", area: "0 m²", speed: "0 m/s" };

  const geometrySource = (contextPoints && contextPoints.length > 100) ? contextPoints : points;
  const { center, radius: pathRadius } = calculateAssetGeometry(geometrySource);

  const H_FOV_RAD = 114 * (Math.PI / 180);
  const V_FOV_RAD = 2 * Math.atan(Math.tan(H_FOV_RAD / 2) * (9 / 16));

  let totalPathLength = 0, totalScannedArea = 0, weightedOverlapSum = 0, frameCount = 0;
  const SAMPLING_INTERVAL = useExplicitPoints ? 0 : 0.5;

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    let nextIdx = i + 1;
    if (SAMPLING_INTERVAL > 0) {
      while (nextIdx < points.length && (points[nextIdx].timestamp - p1.timestamp) < SAMPLING_INTERVAL) nextIdx++;
    }
    if (nextIdx >= points.length) break;
    const p2 = points[nextIdx];

    const distFromCenter = Math.sqrt(Math.pow(p1.x - center.x, 2) + Math.pow(p1.y - center.y, 2));
    const estimatedWallRadius = pathRadius + 1.0;
    const standoff = Math.abs(estimatedWallRadius - distFromCenter);
    const d = Math.max(0.3, standoff);
    const frameW = 2 * d * Math.tan(H_FOV_RAD / 2);
    const frameH = 2 * d * Math.tan(V_FOV_RAD / 2);
    const frameArea = frameW * frameH;

    const dx_w = p2.x - p1.x, dy_w = p2.y - p1.y, dz_w = p2.z - p1.z;
    const distMoved = Math.sqrt(dx_w * dx_w + dy_w * dy_w + dz_w * dz_w);
    const localShift = applyQuaternion({ x: dx_w, y: dy_w, z: dz_w }, { x: -p1.qx, y: -p1.qy, z: -p1.qz, w: p1.qw });

    const overlapW = Math.max(0, frameW - Math.abs(localShift.x));
    const overlapH = Math.max(0, frameH - Math.abs(localShift.y));
    weightedOverlapSum += (overlapW * overlapH) / frameArea;
    frameCount++;
    totalScannedArea += (frameW * distMoved);
    totalPathLength += distMoved;
    i = nextIdx - 1;
  }

  const avgOverlap = frameCount > 0 ? (weightedOverlapSum / frameCount) : 0;
  const duration = points[points.length - 1].timestamp - points[0].timestamp;
  const avgSpeed = duration > 0 ? totalPathLength / duration : 0;

  return {
    coverageStr: ((1.0 + avgOverlap) * 100).toFixed(1) + "%",
    area: totalScannedArea.toFixed(1) + " m²",
    speed: avgSpeed.toFixed(2) + " m/s"
  };
};

import { saveFileToProject } from "./fileSystemService";

export type ReportMode = 'CUSTOMER' | 'REPAIR';

export const generateInspectionReport = async (
  analysis: CrackAnalysis,
  imageUrl: string,
  timeline?: CrackAnalysis[],
  filenamePrefix: string = "report_details_",
  telemetry?: TelemetryPoint[],
  mode: ReportMode = 'CUSTOMER',
  customer?: string,
  asset?: string,
  targetWindow?: Window | null
) => {
  try {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' }) as any;
    const timestampDate = new Date();
    const timestampStr = timestampDate.toLocaleString();
    const reportId = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');

    // Format filename safe for disk — include customer/asset when available
    const safeDate = timestampDate.toISOString().replace(/[:.]/g, '-').split('T')[0];
    const safeCust = customer ? customer.replace(/[^a-z0-9]/gi, '_').toUpperCase() : null;
    const safeAsset = asset ? asset.replace(/[^a-z0-9]/gi, '_').toUpperCase() : null;
    const cleanPrefix = filenamePrefix.replace(/[^a-z0-9_\-]/gi, '_');
    const nameparts = [cleanPrefix, safeCust, safeAsset, safeDate, reportId].filter(Boolean);
    const finalFilename = `${nameparts.join('_')}.pdf`;

    // ── Telemetry Coverage ───────────────────────────────────────────────────
    const fullTelemetry = telemetry || [];
    let activeTelemetry = fullTelemetry, useExplicitPoints = false;
    if (timeline && timeline.length > 1 && fullTelemetry.length > 0) {
      const missionStart = detectMissionStart(fullTelemetry);
      const matched = timeline.map(t => {
        const absTime = missionStart + (t.timestamp || 0);
        let closest = fullTelemetry[0], minDiff = Infinity;
        for (const p of fullTelemetry) { const diff = Math.abs(p.timestamp - absTime); if (diff < minDiff) { minDiff = diff; closest = p; } }
        return closest;
      });
      activeTelemetry = [...new Set(matched)].sort((a, b) => a.timestamp - b.timestamp);
      useExplicitPoints = true;
    } else if (timeline && timeline.length > 0 && fullTelemetry.length > 0) {
      const timestamps = timeline.map(t => t.timestamp || 0);
      const missionStart = detectMissionStart(fullTelemetry);
      const absStart = missionStart + Math.min(...timestamps) - 1.0;
      const absEnd = missionStart + Math.max(...timestamps) + 1.0;
      activeTelemetry = fullTelemetry.filter(p => p.timestamp >= absStart && p.timestamp <= absEnd);
    }
    const { coverageStr, area } = calculatePreciseCoverage(activeTelemetry, fullTelemetry, useExplicitPoints);
    const assetGeom = calculateAssetGeometry(fullTelemetry);
    const firstPoint = fullTelemetry.length > 0 ? fullTelemetry[0] : null;
    const zeroHead = firstPoint ? Math.atan2(firstPoint.y - assetGeom.center.y, firstPoint.x - assetGeom.center.x) : 0;

    const getModeLabel = (m: InspectionMode) => {
      if (m === InspectionMode.GENERAL_NDT) return "Structural Integrity Audit";
      if (m === InspectionMode.CRACK_ONLY) return "Surface Crack Detection";
      if (m === InspectionMode.FOULING_INSPECTION) return "Cleanliness & Fouling Audit";
      if (m === InspectionMode.WELD_INTEGRITY) return "Weld Integrity Evaluation";
      return "Technical Surface Inspection";
    };

    const protocolMode = getModeLabel(analysis.mode || InspectionMode.GENERAL_NDT);
    const severityColors: Record<Severity, [number, number, number]> = {
      [Severity.NONE]: [34, 197, 94],
      [Severity.LOW]: [59, 130, 246],
      [Severity.MEDIUM]: [234, 179, 8],
      [Severity.HIGH]: [249, 115, 22],
      [Severity.CRITICAL]: [220, 38, 38],
    };
    const severityRank: Record<Severity, number> = { [Severity.CRITICAL]: 4, [Severity.HIGH]: 3, [Severity.MEDIUM]: 2, [Severity.LOW]: 1, [Severity.NONE]: 0 };

    // Safe lookup — returns GEA_MID (slate-400) if the severity value is not in the map.
    // Prevents `TypeError: ... is not iterable` when a finding arrives with an unmapped severity.
    const getSeverityColor = (sev: any): [number, number, number] => severityColors[sev as Severity] || GEA_MID;

    let aggregatedFindings: (Finding & { absoluteTime?: number; frameImage?: string })[] = [];
    const seen = new Set<string>();

    const processFinding = (f: Finding, timestamp: number, frame: string) => {
      const displayType = getDisplayTitle(f.type);
      const key = `${displayType}-${f.box?.join(',')}`;
      if (!seen.has(key)) {
        seen.add(key);
        aggregatedFindings.push({
          ...f,
          type: displayType,
          absoluteTime: f.timestamp ?? timestamp,
          frameImage: (f as any).frameImage || frame
        });
      }
    };

    // 1. Process Timeline (Mission-wide)
    if (timeline && timeline.length > 0) {
      timeline.forEach(t => t.findings?.forEach(f => processFinding(f, t.timestamp, t.frameImage || imageUrl)));
    }

    // 2. Process Current Analysis (Ensures findings in current focus are present)
    if (analysis.findings && analysis.findings.length > 0) {
      analysis.findings.forEach(f => processFinding(f, f.timestamp ?? analysis.timestamp, (f as any).frameImage || imageUrl));
    }

    // ── Metric Calculation ──────────────────────────────────────────────────
    const severityCount = { [Severity.CRITICAL]: 0, [Severity.HIGH]: 0, [Severity.MEDIUM]: 0, [Severity.LOW]: 0, [Severity.NONE]: 0 };
    const categoryCount: Record<string, number> = {};

    aggregatedFindings.forEach(f => {
      severityCount[f.severity]++;
      const cat = f.type;
      categoryCount[cat] = (categoryCount[cat] || 0) + 1;
    });

    const isNominal = aggregatedFindings.length === 0;
    const finalSeverity = isNominal ? Severity.NONE : (analysis.severity || Severity.NONE);

    const getGeneralEvaluation = (sev: Severity) => {
      if (isNominal) return "The comprehensive technical audit concludes that the asset is currently in an optimal state of structural preservation. Advanced signal processing and visual data analysis have confirmed that all critical surface regions are free from detectable anomalies, erosive patterns, or structural deviations. The asset demonstrates high-level integrity consistent with peak operational standards. To maintain this baseline, we recommend continuing with standard preventative maintenance and routine periodic monitoring as per the established annual lifecycle management protocol.";

      if (sev === Severity.CRITICAL) return "URGENT STRUCTURAL COMPROMISE IDENTIFIED. During this mission, the GEA-NDT Neural Engine isolated critical failure indicators that represent a significant risk to the operational safety and structural viability of the asset. The detected anomalies suggest an advanced state of material degradation or mechanical failure that exceeds safe operating tolerances. Continued operation under current conditions is not advised. We recommend an immediate suspension of service followed by a manual engineering verification and restorative intervention to mitigate the risk of catastrophic asset loss or safety incident.";

      if (sev === Severity.HIGH) return "Significant degradation patterns have been identified across key surface coordinates. While the primary structural integrity remains functional at this time, the observed trends indicate an active and accelerating path of deterioration. These findings are consistent with areas under high thermal or mechanical stress that now require high-priority attention. Failure to implement corrective measures in the near-term maintenance window could lead to a rapid transition into a critical failure state. Engineering oversight is advised for the planning of focused repair cycles.";

      if (sev === Severity.MEDIUM) return "The technical audit has localized several surface anomalies and material irregularities that warrant formal observation. These findings suggest the early-to-mid stages of environmental or operational wear, which, while not currently threatening the asset's immediate performance, do signify a departure from nominal conditions. We recommend integrating these specific regions into the next scheduled maintenance cycle. Enhanced monitoring frequency should be applied to these coordinates to track the rate of change and ensure that degradation remains within manageable thresholds.";

      return "The asset health is currently rated as good, though minor superficial anomalies were recorded during the inspection flow. These technical findings relate primarily to baseline surface coating irregularities or minor localized wear that does not impact the structural or operational throughput of the asset. No immediate engineering intervention is required. These observations have been logged into the asset's digital twin record for historical benchmarking; they should be verified during the next routine maintenance check to ensure continued nominal behavior.";
    };

    const getTimeRecommendation = (sev: Severity) => {
      if (isNominal) return "Next scheduled annual inspection (12 months).";
      if (sev === Severity.CRITICAL) return "IMMEDIATE (Shutdown / Repair required before next cycle).";
      if (sev === Severity.HIGH) return "Within 30 Days (Scheduled maintenance window).";
      if (sev === Severity.MEDIUM) return "Within 3 to 6 Months (Next quarterly review).";
      return "Next routine maintenance (6-12 months).";
    };

    // ── GEA HEADER ──────────────────────────────────────────────────────────
    const drawHeader = (doc: any, pageTitle: string) => {
      // Deep navy top bar
      doc.setFillColor(...GEA_SLATE); doc.rect(0, 0, 210, 28, "F");
      // GEA brand block (left) — solid blue square
      doc.setFillColor(...GEA_BLUE); doc.rect(0, 0, 28, 28, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...WHITE);
      doc.text("GEA", 14, 17, { align: "center" });
      // Title text in white
      doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(...WHITE);
      doc.text("NDT-CHECK PRO  |  GEA Engineering Services", 35, 12);
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...GEA_MID);
      doc.text(`${pageTitle.toUpperCase()}  |  REF: GEA-NDT-${reportId}`, 35, 19);
      // Coverage badge (top right white pill)
      if (coverageStr !== "N/A") {
        doc.setFillColor(...WHITE); doc.roundedRect(154, 5, 50, 18, 2, 2, "F");
        doc.setFont("helvetica", "bold"); doc.setFontSize(5.5); doc.setTextColor(...GEA_BLUE);
        doc.text("GEOMETRIC COVERAGE", 156, 10);
        doc.setFontSize(7); doc.setTextColor(...GEA_SLATE);
        doc.text(`AVG: ${coverageStr}`, 156, 15); doc.text(`AREA: ${area}`, 156, 20);
      }
      // Bottom accent line
      doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.5); doc.line(0, 28, 210, 28);
    };

    // ── GEA FOOTER ──────────────────────────────────────────────────────────
    const drawFooter = (doc: any) => {
      doc.setFillColor(...GEA_LIGHT); doc.rect(0, 283, 210, 14, "F");
      doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.4); doc.line(0, 283, 210, 283);
      doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...GEA_MID);
      doc.text("GEA Group  ·  NDT-CHECK PRO V6.0  ·  Confidential Inspection Report", 15, 289);
      doc.setTextColor(...GEA_BLUE); doc.setFont("helvetica", "bold");
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${doc.internal.getNumberOfPages()}`, 195, 289, { align: "right" });
    };

    const drawAnnotationBox = (doc: any, box: [number, number, number, number], imgX: number, imgY: number, imgW: number, imgH: number, color: [number, number, number], label: string) => {
      if (!box || box.length < 4) return;
      const [ymin, xmin, ymax, xmax] = box;
      const x = imgX + (xmin / 1000) * imgW, y = imgY + (ymin / 1000) * imgH, w = ((xmax - xmin) / 1000) * imgW, h = ((ymax - ymin) / 1000) * imgH;
      doc.setDrawColor(color[0], color[1], color[2]); doc.setLineWidth(0.8); doc.rect(x, y, w, h);
      if (label) { doc.setFillColor(color[0], color[1], color[2]); doc.rect(x, y - 4, doc.getTextWidth(label) + 2, 4, "F"); doc.setTextColor(255, 255, 255); doc.setFontSize(5); doc.text(label, x + 1, y - 1); }
    };

    // ── PAGE 1: COVER ────────────────────────────────────────────────────────
    // Dark slate cover
    doc.setFillColor(...GEA_SLATE); doc.rect(0, 0, 210, 297, "F");
    // Top GEA stripe
    doc.setFillColor(...GEA_BLUE); doc.rect(0, 0, 210, 60, "F");
    // GEA logo block
    doc.setFillColor(...WHITE); doc.rect(15, 10, 40, 40, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(28); doc.setTextColor(...GEA_BLUE);
    doc.text("GEA", 35, 36, { align: "center" });
    // Title
    doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(...WHITE);
    doc.text("NDT INSPECTION REPORT", 105, 90, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(...GEA_MID);
    doc.text(`${mode === 'REPAIR' ? 'REPAIR GUIDANCE' : 'CUSTOMER AUDIT'} · BUILD V6.0`, 105, 101, { align: "center" });
    // Separator
    doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(1); doc.line(50, 108, 160, 108);
    // Meta block
    doc.setFillColor(15, 23, 42); doc.roundedRect(20, 120, 170, 70, 3, 3, "F");
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...GEA_MID);
    doc.text("Protocol:", 30, 133); doc.text("Customer:", 30, 141); doc.text("Asset:", 30, 149); doc.text("Target:", 30, 157); doc.text("Date:", 30, 165); doc.text("Ref:", 30, 173);
    doc.setFont("helvetica", "bold"); doc.setTextColor(...WHITE);
    doc.text(protocolMode, 70, 133);
    doc.text(customer || "—", 70, 141);
    doc.text(asset || "—", 70, 149);
    doc.text(analysis.videoName || "Static Image Sequence", 70, 157);
    doc.text(timestampStr, 70, 165); doc.text(`GEA-NDT-${reportId}`, 70, 173);
    // Confidential footer on cover
    doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(...GEA_MID);
    doc.text("PROPRIETARY & CONFIDENTIAL — GEA Group · Authorised Personnel Only", 105, 275, { align: "center" });

    // ── PAGE 2: ASSET HEALTH & SUMMARY ───────────────────────────────────────
    doc.addPage();
    drawHeader(doc, `${mode} Executive Summary`);

    // 1. Asset Health Score / Rating (Customer Specific)
    if (mode === 'CUSTOMER') {
      doc.setFillColor(...GEA_LIGHT); doc.rect(15, 35, 180, 50, "F");
      doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.3); doc.rect(15, 35, 180, 50);

      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...GEA_BLUE);
      doc.text("ENGINEERING CONDITION EVALUATION", 20, 45);

      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...GEA_DARK_TXT);
      const evalText = doc.splitTextToSize(getGeneralEvaluation(finalSeverity), 170);
      doc.text(evalText, 20, 53);

      doc.setFont("helvetica", "bold"); doc.text("RECOMMENDED ACTION TIMELINE:", 20, 75);
      doc.setTextColor(...getSeverityColor(finalSeverity));
      doc.text(getTimeRecommendation(finalSeverity).toUpperCase(), 85, 75);

      // 2. Findings Distribution Matrix
      doc.setFillColor(255, 255, 255); doc.rect(15, 90, 180, 45, "F");
      doc.setDrawColor(...GEA_MID); doc.rect(15, 90, 180, 45);

      doc.setFontSize(9); doc.setTextColor(...GEA_BLUE); doc.text("FINDINGS DISTRIBUTION BY SEVERITY", 20, 100);

      const sevItems = [Severity.CRITICAL, Severity.HIGH, Severity.MEDIUM, Severity.LOW];
      sevItems.forEach((sev, idx) => {
        const xPos = 20 + (idx * 45);
        doc.setFillColor(...getSeverityColor(sev)); doc.rect(xPos, 105, 35, 20, "F");
        doc.setTextColor(...WHITE); doc.setFontSize(12); doc.text(severityCount[sev].toString(), xPos + 17.5, 117, { align: "center" });
        doc.setFontSize(6); doc.text(sev.toUpperCase(), xPos + 17.5, 122, { align: "center" });
      });

      // 3. Category Breakdown
      doc.setFontSize(9); doc.setTextColor(...GEA_BLUE); doc.text("CATEGORICAL BREAKDOWN", 20, 142);
      let catY = 150;
      Object.entries(categoryCount).forEach(([cat, count]) => {
        if (catY > 185) return;
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...GEA_DARK_TXT);
        doc.text(cat.toUpperCase(), 20, catY);
        doc.setFont("helvetica", "bold"); doc.text(count.toString(), 190, catY, { align: "right" });
        doc.setDrawColor(...GEA_LIGHT); doc.setLineWidth(0.1); doc.line(20, catY + 1.5, 190, catY + 1.5);
        catY += 6;
      });

      doc.addPage();
      drawHeader(doc, "Mission Spatiotemporal Overview");
    }

    // Existing Summary card (Now on Page 3 or 2 based on mode)
    doc.setFillColor(...GEA_LIGHT); doc.rect(15, 35, 180, 60, "F");
    doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.3); doc.rect(15, 35, 180, 60);
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...GEA_BLUE);
    doc.text("V6.0 Mission Context", 20, 47);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...GEA_MID);
    doc.text("Audit Target:", 20, 57); doc.text("Date/Time:", 20, 64); doc.text("Confidence (V6.0):", 20, 71); doc.text("Total Anomalies:", 20, 78);
    doc.setFont("helvetica", "bold"); doc.setTextColor(...GEA_DARK_TXT);
    doc.text(analysis.videoName || "Static Sequence", 75, 57);
    doc.text(timestampStr, 75, 64);
    doc.text(`${(analysis.confidenceScore * 100).toFixed(1)}%`, 75, 71);
    doc.text(aggregatedFindings.length.toString(), 75, 78);

    // Verdict banner
    const finalSeverityLabel = finalSeverity;
    const statusColor = getSeverityColor(finalSeverityLabel);
    doc.setFillColor(...statusColor); doc.rect(15, 100, 180, 18, "F");
    doc.setFillColor(...GEA_BLUE); doc.rect(15, 100, 5, 18, "F"); // left accent
    doc.setTextColor(...WHITE); doc.setFontSize(12); doc.setFont("helvetica", "bold");
    doc.text(isNominal ? "VERDICT: SURFACE NOMINAL" : `AUDIT STATE: ${finalSeverity} DETECTED`, 108, 111, { align: "center" });

    // Primary image
    const mostCritical = [...aggregatedFindings].sort((a, b) => severityRank[b.severity] - severityRank[a.severity])[0];
    const summaryImageUrl = (mostCritical?.frameImage) || imageUrl;
    if (summaryImageUrl && summaryImageUrl.startsWith('data:image')) {
      const imgX = 15, imgY = 125, imgW = 180, imgH = 100;
      const shrunken = await shrinkImage(summaryImageUrl, 1200);
      doc.addImage(shrunken, "JPEG", imgX, imgY, imgW, imgH);
      // GEA blue frame around image
      doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.5); doc.rect(imgX, imgY, imgW, imgH);
      if (!isNominal && mostCritical && mostCritical.box) {
        const catColor = getCategoryColor(mostCritical.type);
        drawAnnotationBox(doc, mostCritical.box, imgX, imgY, imgW, imgH, catColor, `PRIMARY ANOMALY [${mostCritical.severity}]`);
      }
    }
    drawFooter(doc);

    // ── FINDINGS PAGES ────────────────────────────────────────────────────────
    if (!isNominal) {
      const chunkSize = 4;
      for (let i = 0; i < aggregatedFindings.length; i += chunkSize) {
        const chunk = aggregatedFindings.slice(i, i + chunkSize);
        doc.addPage();
        drawHeader(doc, `${mode} Detailed Findings Log`);
        for (let index = 0; index < chunk.length; index++) {
          const finding = chunk[index];
          const col = index % 2, row = Math.floor(index / 2);
          const cardWidth = 85, imgHeight = 48, barHeight = 6;
          const cardX = 15 + (col * (cardWidth + 10)), cardY = 35 + (row * 125);
          const color = getSeverityColor(finding.severity);

          // GEA blue top stripe on card
          doc.setFillColor(...GEA_BLUE); doc.rect(cardX, cardY, cardWidth, barHeight + 1, "F");
          // Severity color indicator (right strip)
          doc.setFillColor(...color); doc.rect(cardX + cardWidth - 5, cardY, 5, barHeight + 1, "F");
          doc.setTextColor(...WHITE); doc.setFontSize(7); doc.setFont("courier", "bold");

          const timeText = finding.absoluteTime !== undefined ? formatMMSS(finding.absoluteTime) : 'N/A';
          let bannerMetadata = timeText;
          if (mode === 'REPAIR') {
            const coords = finding.spatialCoords || (finding as any).pos;
            if (coords && coords.z !== undefined) bannerMetadata += ` | H:${coords.z.toFixed(1)}m`;

            // Always recalculate azimuth to ensure mission-start consistency (zeroHeading rule)
            if (coords && coords.x !== undefined && coords.y !== undefined) {
              const theta = Math.atan2(coords.y - assetGeom.center.y, coords.x - assetGeom.center.x);
              let deg = ((zeroHead - theta) * 180 / Math.PI) % 360;
              if (deg < 0) deg += 360;
              bannerMetadata += ` | ${Math.round(deg)}°`;
            } else if (finding.azimuth !== undefined) {
              bannerMetadata += ` | ${Math.round(finding.azimuth)}°`;
            }
          }
          doc.text(bannerMetadata, cardX + 2, cardY + 4);
          doc.text(`${finding.severity}`, cardX + cardWidth - 8, cardY + 4, { align: 'right' });

          doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.5); doc.rect(cardX, cardY + barHeight, cardWidth, imgHeight);
          if (finding.frameImage) {
            try {
              const shrunken = await shrinkImage(finding.frameImage, 600);
              const catColor = getCategoryColor(finding.type);
              doc.addImage(shrunken, "JPEG", cardX + 0.5, cardY + barHeight + 0.5, cardWidth - 1, imgHeight - 1);
              if (finding.box) drawAnnotationBox(doc, finding.box, cardX + 0.5, cardY + barHeight + 0.5, cardWidth - 1, imgHeight - 1, catColor, "");
            } catch (e) {
              doc.setFillColor(240, 240, 240); doc.rect(cardX + 0.5, cardY + barHeight + 0.5, cardWidth - 1, imgHeight - 1, "F");
              doc.setTextColor(...GEA_MID); doc.text("IMG_ERR", cardX + cardWidth / 2, cardY + barHeight + imgHeight / 2, { align: 'center' });
            }
          }
          const textStartY = cardY + barHeight + imgHeight + 5;
          const catColor = getCategoryColor(finding.type);
          doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...catColor);
          doc.text(finding.type.toUpperCase(), cardX, textStartY);
          doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...GEA_DARK_TXT);
          const dL = doc.splitTextToSize(finding.description || "No description provided.", cardWidth); doc.text(dL, cardX, textStartY + 4);
          const dH = dL.length * 3;
          doc.setFont("helvetica", "bold"); doc.text("Proposed Solution:", cardX, textStartY + 4 + dH + 3);
          doc.setFont("helvetica", "normal"); const sL = doc.splitTextToSize(finding.recommendation || "Maintain standard inspection intervals.", cardWidth); doc.text(sL, cardX, textStartY + 4 + dH + 7);
          const sH = sL.length * 3;
          doc.setFont("helvetica", "bold"); doc.text("Preventive Action:", cardX, textStartY + 4 + dH + 7 + sH + 3);
          doc.setFont("helvetica", "normal"); doc.text(doc.splitTextToSize(finding.prevention || "N/A", cardWidth), cardX, textStartY + 4 + dH + 7 + sH + 7);
        }
        drawFooter(doc);
      }
    }

    const pdfBlob = doc.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);

    /**
     * BYPASS POPUP BLOCKERS
     * Browser security blocks new tabs that open after long async operations (like processing 3D snapshots).
     * We open a blank window immediately and then redirect it when the PDF is ready.
     */
    const reportWindow = targetWindow || window.open('about:blank', '_blank');
    if (reportWindow) {
      reportWindow.location.href = blobUrl;
    } else {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.click();
    }

    // 2. Auto-save to Local Project in background
    setTimeout(async () => {
      try {
        const savedLocally = await saveFileToProject('05_Generated_Reports', finalFilename, pdfBlob);
        if (savedLocally) {
          console.log(`[GEA-NDT] Report auto-saved to Local Project: ${finalFilename}`);
        }
      } catch (e) {
        console.warn("[GEA-NDT] Background save failed", e);
      }
    }, 0);

    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) { console.error("[GEA-NDT] PDF generation error", err); throw err; }
};
